import dayjs from 'dayjs';
import '@utils/dayjsInit'; // PEPITA-825: ensure dayjs plugins are extended before .tz().guess() below.

const defaultTimeZone = dayjs.tz.guess();

const defaultCurrentTime = (flags = {}) => {
  if (flags.isTest) {
    return dayjs('2023-10-17'); // test date for unit testing purposes.
  }
  return dayjs().utc().hour(17).startOf('hour');
};

const DEFAULT_REQUEST = (user, flags = {}) => {
  const currentTime = defaultCurrentTime(flags);

  // Add open for questions round 14 days from start of creation.
  const openForViewingAt = currentTime.clone().add(14, 'days');

  const request = {
    contract_length: 3,
    contract_length_unit: 'years',
    currency: 'USD',
    description: '',
    multi_award: true,
    name: '',
    open_for_viewing_at: openForViewingAt,
    send_event_reminders: true,
    time_zone: defaultTimeZone,
    ui_config: user.global.requests.ui_config,
  };

  // Update ui_config.lots.show_discount based on company settings
  request.ui_config.lots[user.global.lots.ui_config_keys.show_discount] = user.company.company_options.find(
    option => option.name === 'Show Discount Column'
  )?.option_value !== 'false';

  if (user?.assigned_business_unit){
    request.business_unit_id = user?.assigned_business_unit.id;
  }

  return request;
};

const buildDefaultBiddingEvent = (user, flags = {}) => {
  const currentTime = defaultCurrentTime(flags);
  const eventFormat = 'rounds';

  return {
    ...user.global.bidding_events.default_options,
    end_at: currentTime.clone().add(21, 'days'),
    event_format: eventFormat,
    start_at: currentTime.clone().add(14, 'days'),
  };
};

const buildDefaultRound = (user, request = null, flags = {}) => {
  const timeToUseForCalcualations = request?.open_for_viewing_at || defaultCurrentTime(flags);
  const startTime = timeToUseForCalcualations.clone().add(14, 'days');
  const endTime = startTime.clone().add(7, 'days');

  return {
    ...user.global.bidding_events.default_round,
    end_time: endTime,
    start_time: startTime,
  };
};

// RFI - No bidding event & collect pricing off
export const buildInformationRequestParams = (user, flags = {}) => {
  const params = { ...DEFAULT_REQUEST(user, flags) };
  const LOTS_CONFIG_KEYS = user.global.lots.ui_config_keys;

  params.ui_config.lots[LOTS_CONFIG_KEYS.show_pricing_columns] = false;
  params.bidding_event_attributes = null;

  return params;
};

// Old RFP - Deadline event, 1 week long
// New RFP - Rounds event, 1 question round, 2 bidding rounds.
export const buildProposalRequestParams = (user, flags = {}, t) => {
  const params = { ...DEFAULT_REQUEST(user, flags) };
  const LOTS_CONFIG_KEYS = user.global.lots.ui_config_keys;

  params.ui_config.lots[LOTS_CONFIG_KEYS.show_pricing_columns] = true;
  const round1 = buildDefaultRound(user, params);

  const round2 = {
    ...round1,
    end_time: round1.end_time.clone().startOf('hour').add(7, 'days'),
    name: t('bid_request_default.round_name', { number: 2 }),
    number: 2,
    start_time: round1.end_time.clone().startOf('hour'),
  };

  const endTime = round2.end_time;

  params.start_time = round1.start_time;
  params.end_time = endTime;
  params.award_phase_end_at = endTime.clone().add(7, 'days');
  params.bidding_event_attributes = {
    ...buildDefaultBiddingEvent(user, flags),
    end_at: endTime,
    has_price_guidance_round: false,
    round_count: 2,
    rounds_attributes: [round1, round2],
    start_at: round1.start_time,
  };

  return params;
};

// Price Update - Single round, pricing enabled, one-off event
// No questions round (open_for_viewing_at = null) and no award phase
export const buildPriceUpdateRequestParams = (user, flags = {}) => {
  const params = { ...DEFAULT_REQUEST(user, flags) };
  const LOTS_CONFIG_KEYS = user.global.lots.ui_config_keys;

  params.request_type = 'price_update';
  params.ui_config.lots[LOTS_CONFIG_KEYS.show_pricing_columns] = true;
  params.one_off = true;
  params.open_for_viewing_at = null;
  params.award_phase_end_at = null;

  const round1 = buildDefaultRound(user, params, flags);

  const endTime = round1.end_time;

  params.start_time = round1.start_time;
  params.end_time = endTime;
  params.bidding_event_attributes = {
    ...buildDefaultBiddingEvent(user, flags),
    end_at: endTime,
    has_price_guidance_round: false,
    round_count: 1,
    rounds_attributes: [round1],
    should_skip_weekends: true,
    start_at: round1.start_time,
  };

  return params;
};

export const buildDefaultRequestParams = (user, requestType, flags, t) => {
  // Default to 'Proposal' configuration
  let params = buildProposalRequestParams(user, flags, t);

  if (requestType === 'information') {
    params = buildInformationRequestParams(user, flags);
  }

  if (requestType === 'price_update') {
    params = buildPriceUpdateRequestParams(user, flags);
  }

  return params;
};

export default DEFAULT_REQUEST;
